import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { ArrowLeft, Loader2, Save, ChevronDown, ChevronRight, Plus, Edit as EditIcon, Trash2, X, Layers, Search, Link2 } from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete, slugify, formatMXN } from '../lib/api'
import { productImageUrl } from '../lib/image-url'
import { ImageDropzone } from '../components/ImageDropzone'
import { RichEditor } from '../components/RichEditor'

interface ProductoData {
  data: {
    id: string
    sku: string
    slug: string
    nombre: string
    descripcion: string
    categoriaId: string
    brandId: string
    precio: number
    precio2: number | null
    precio3: number | null
    precio4: number | null
    precioOferta: number | null
    costo: number
    ivaRate?: number
    stock: number
    pesoKg: number
    imagen: string
    destacado: boolean
    enOferta: boolean
    ofertaSemana: boolean
    activo: boolean
    metaTitulo: string
    metaDescripcion: string
    metaKeywords: string
    tags: string
    imagen2: string
    imagen3: string
    imagen4: string
    imagen5: string
    imagenBaja: string
    dimLargo: number
    dimAncho: number
    dimAlto: number
    dimTexto: string
    anioDesde: string
    anioHasta: string
    marcasTexto: string
    modelosTexto: string
    esNuevo: boolean
    esHot: boolean
    inhiboCompra: boolean
    linea: string
    origenRepuesto: string
    marcaRepuesto: string
    marcaRepuestoId: string
    proveedorId: string
    precioRegular: number
    skuKit: string
    skuAnterior: string
    nombreAnterior: string
  }
}

interface CategoriasData {
  data: Array<{ id: string; nombre: string; nivel?: number }>
}

interface MarcasData {
  data: Array<{ id: string; nombre: string }>
}

interface FormState {
  sku: string
  slug: string
  name: string
  description: string
  category_id: string
  brand_id: string
  price: string
  price_2: string
  price_3: string
  price_4: string
  sale_price: string
  cost: string
  iva: string
  stock: string
  weight_kg: string
  image: string
  is_featured: boolean
  is_on_sale: boolean
  is_week_sale: boolean
  is_active: boolean
  meta_title: string
  meta_description: string
  meta_keywords: string
  tags: string
  image2: string
  image3: string
  image4: string
  image5: string
  image_low: string
  dim_length: string
  dim_width: string
  dim_height: string
  dim_text: string
  year_from: string
  year_to: string
  compat_brands: string
  compat_models: string
  is_new: boolean
  is_hot: boolean
  inhibit_purchase: boolean
  line: string
  part_origin: string
  part_brand: string
  part_brand_id: string
  supplier_id: string
  regular_price: string
  sku_kit: string
  previous_sku: string
  previous_name: string
}

export function ProductoEditar() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState | null>(null)
  const [slugTouched, setSlugTouched] = useState(false)

  const { data, isLoading, error } = useQuery<ProductoData>({
    queryKey: ['producto', id],
    queryFn: () => apiGet(`/api/admin/productos/${id}`),
  })

  const { data: categorias } = useQuery<CategoriasData>({
    queryKey: ['admin-categorias-min'],
    queryFn: () => apiGet('/api/admin/categorias?tree=1'),
    staleTime: 5 * 60 * 1000,
  })

  const { data: marcas } = useQuery<MarcasData>({
    queryKey: ['admin-marcas-min'],
    queryFn: () => apiGet('/api/admin/marcas?page=1'),
    staleTime: 5 * 60 * 1000,
  })

  useEffect(() => {
    if (data && !form) {
      const p = data.data
      setForm({
        sku: p.sku,
        slug: p.slug,
        name: p.nombre,
        description: p.descripcion ?? '',
        category_id: p.categoriaId,
        brand_id: p.brandId,
        price: String(p.precio ?? 0),
        price_2: p.precio2 != null ? String(p.precio2) : '',
        price_3: p.precio3 != null ? String(p.precio3) : '',
        price_4: p.precio4 != null ? String(p.precio4) : '',
        sale_price: p.precioOferta != null ? String(p.precioOferta) : '',
        cost: String(p.costo ?? 0),
        iva: String(Math.round((p.ivaRate ?? 0.16) * 100)),
        stock: String(p.stock ?? 0),
        weight_kg: String(p.pesoKg ?? 0),
        image: p.imagen ?? '',
        is_featured: p.destacado,
        is_on_sale: p.enOferta,
        is_week_sale: p.ofertaSemana,
        is_active: p.activo,
        meta_title: p.metaTitulo ?? '',
        meta_description: p.metaDescripcion ?? '',
        meta_keywords: p.metaKeywords ?? '',
        tags: p.tags ?? '',
        image2: p.imagen2 ?? '',
        image3: p.imagen3 ?? '',
        image4: p.imagen4 ?? '',
        image5: p.imagen5 ?? '',
        image_low: p.imagenBaja ?? '',
        dim_length: String(p.dimLargo ?? 0),
        dim_width: String(p.dimAncho ?? 0),
        dim_height: String(p.dimAlto ?? 0),
        dim_text: p.dimTexto ?? '',
        year_from: p.anioDesde ?? '',
        year_to: p.anioHasta ?? '',
        compat_brands: p.marcasTexto ?? '',
        compat_models: p.modelosTexto ?? '',
        is_new: p.esNuevo,
        is_hot: p.esHot,
        inhibit_purchase: p.inhiboCompra,
        line: p.linea ?? '',
        part_origin: p.origenRepuesto ?? '',
        part_brand: p.marcaRepuesto ?? '',
        part_brand_id: p.marcaRepuestoId ?? '',
        supplier_id: p.proveedorId ?? '',
        regular_price: String(p.precioRegular ?? 0),
        sku_kit: p.skuKit ?? '',
        previous_sku: p.skuAnterior ?? '',
        previous_name: p.nombreAnterior ?? '',
      })
    }
  }, [data, form])

  const updateMut = useMutation({
    mutationFn: (payload: any) => apiPut(`/api/admin/productos/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto', id] })
      qc.invalidateQueries({ queryKey: ['productos'] })
      navigate({ to: '/productos/$id', params: { id } })
    },
  })

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!form) return
    const payload = {
      sku: form.sku.trim(),
      slug: form.slug.trim() || slugify(form.name),
      name: form.name.trim(),
      description: form.description.trim() || null,
      category_id: form.category_id || null,
      brand_id: form.brand_id || null,
      price: Number(form.price) || 0,
      price_2: form.price_2 !== '' ? Number(form.price_2) : null,
      price_3: form.price_3 !== '' ? Number(form.price_3) : null,
      price_4: form.price_4 !== '' ? Number(form.price_4) : null,
      sale_price: form.sale_price !== '' ? Number(form.sale_price) : null,
      cost: Number(form.cost) || 0,
      tax_rate: form.iva !== '' ? Number(form.iva) / 100 : 0.16,
      stock: parseInt(form.stock) || 0,
      weight_kg: Number(form.weight_kg) || 0,
      image: form.image.trim(),
      is_featured: form.is_featured,
      is_on_sale: form.is_on_sale,
      is_week_sale: form.is_week_sale,
      is_active: form.is_active,
      meta_title: form.meta_title.trim() || null,
      meta_description: form.meta_description.trim() || null,
      meta_keywords: form.meta_keywords.trim() || null,
      tags: form.tags.trim() || null,
      image2: form.image2.trim() || null,
      image3: form.image3.trim() || null,
      image4: form.image4.trim() || null,
      image5: form.image5.trim() || null,
      image_low: form.image_low.trim() || null,
      dimensions_length: Number(form.dim_length) || 0,
      dimensions_width: Number(form.dim_width) || 0,
      dimensions_height: Number(form.dim_height) || 0,
      dimensions_text: form.dim_text.trim() || null,
      year_from: form.year_from.trim() || null,
      year_to: form.year_to.trim() || null,
      compatible_brands_text: form.compat_brands.trim() || null,
      compatible_models_text: form.compat_models.trim() || null,
      is_new: form.is_new,
      is_hot: form.is_hot,
      inhibit_purchase: form.inhibit_purchase,
      line: form.line.trim() || null,
      part_origin: form.part_origin.trim() || null,
      part_brand: form.part_brand.trim() || null,
      part_brand_id: form.part_brand_id || null,
      supplier_id: form.supplier_id || null,
      regular_price: Number(form.regular_price) || 0,
      sku_kit: form.sku_kit.trim() || null,
      previous_sku: form.previous_sku.trim() || null,
      previous_name: form.previous_name.trim() || null,
    }
    updateMut.mutate(payload)
  }

  if (isLoading || !form) {
    return (
      <>
        <Topbar title="Editar producto" subtitle="Cargando…" />
        <div className="grid place-items-center py-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
        </div>
      </>
    )
  }

  if (error) {
    return (
      <>
        <Topbar title="Editar producto" subtitle="Error" />
        <div className="p-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            No se pudo cargar el producto.
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Topbar
        title={`Editar: ${form.name}`}
        subtitle={`SKU: ${form.sku}`}
        actions={
          <>
            <button
              type="button"
              onClick={() => navigate({ to: '/productos/$id', params: { id } })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Cancelar
            </button>
            <button
              type="submit"
              form="producto-form"
              disabled={updateMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {updateMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Save className="size-3.5" />
              )}
              Guardar
            </button>
          </>
        }
      />

      <form id="producto-form" onSubmit={onSubmit} className="space-y-4 p-6">
        {updateMut.isError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {(updateMut.error as Error).message}
          </div>
        )}

        <ProductForm
          form={form}
          setField={setField}
          slugTouched={slugTouched}
          setSlugTouched={setSlugTouched}
          categorias={categorias?.data ?? []}
          marcas={marcas?.data ?? []}
        />
      </form>

      <div className="px-6 pb-10 space-y-4">
        <VariantesSection productId={id} />
        <RecomendacionesSection productId={id} />
      </div>
    </>
  )
}

export default ProductoEditar

// ----------------------------------------------------------------------
// Componente compartido del formulario, reusado en ProductoNuevo
// ----------------------------------------------------------------------

export function ProductForm({
  form,
  setField,
  slugTouched,
  setSlugTouched,
  categorias,
  marcas,
}: {
  form: FormState
  setField: <K extends keyof FormState>(key: K, value: FormState[K]) => void
  slugTouched: boolean
  setSlugTouched: (b: boolean) => void
  categorias: Array<{ id: string; nombre: string; nivel?: number }>
  marcas: Array<{ id: string; nombre: string }>
}) {
  const [catFilter, setCatFilter] = useState('')
  const catsFiltradas = useMemo(() => {
    const q = catFilter.trim().toLowerCase()
    return q ? categorias.filter((c) => c.nombre.toLowerCase().includes(q)) : categorias
  }, [categorias, catFilter])
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {/* Info general */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Información general</h3>
        <div className="space-y-3">
          <Field label="Nombre" required>
            <input
              type="text"
              value={form.name}
              onChange={(e) => {
                setField('name', e.target.value)
                if (!slugTouched) setField('slug', slugify(e.target.value))
              }}
              className={inputCls}
              required
              maxLength={255}
            />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="SKU" required>
              <input
                type="text"
                value={form.sku}
                onChange={(e) => setField('sku', e.target.value)}
                className={`${inputCls} font-mono`}
                required
                maxLength={80}
              />
            </Field>
            <Field label="Slug" hint="URL amigable">
              <input
                type="text"
                value={form.slug}
                onChange={(e) => {
                  setField('slug', e.target.value)
                  setSlugTouched(true)
                }}
                className={`${inputCls} font-mono text-xs`}
                maxLength={180}
              />
            </Field>
          </div>
          <Field label="Descripción">
            <RichEditor
              value={form.description}
              onChange={(v) => setField('description', v)}
              placeholder="Describe el producto…"
              minHeight={260}
            />
          </Field>
        </div>
      </div>

      {/* Toggles */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Estado y visibilidad</h3>
        <div className="space-y-3">
          <Toggle
            label="Activo"
            hint="Visible en el catálogo"
            checked={form.is_active}
            onChange={(b) => setField('is_active', b)}
          />
          <Toggle
            label="Destacado"
            hint="Aparece en sección destacados"
            checked={form.is_featured}
            onChange={(b) => setField('is_featured', b)}
          />
          <Toggle
            label="En oferta"
            hint="Aplica precio de oferta"
            checked={form.is_on_sale}
            onChange={(b) => setField('is_on_sale', b)}
          />
          <Toggle
            label="Oferta de la semana"
            hint="Aparece en la banda del home"
            checked={form.is_week_sale}
            onChange={(b) => setField('is_week_sale', b)}
          />
          <Toggle
            label="Nuevo"
            hint="Badge 'Nuevo'"
            checked={form.is_new}
            onChange={(b) => setField('is_new', b)}
          />
          <Toggle
            label="Hot"
            hint="Badge 'Hot' / trending"
            checked={form.is_hot}
            onChange={(b) => setField('is_hot', b)}
          />
          <Toggle
            label="Inhibir compra"
            hint="Mostrar pero sin botón comprar"
            checked={form.inhibit_purchase}
            onChange={(b) => setField('inhibit_purchase', b)}
          />
        </div>
      </div>

      {/* Precios y stock */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Precios y stock</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Precio 1 · Público" required>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.price}
              onChange={(e) => setField('price', e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Precio 2 · Mayoreo">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="—"
              value={form.price_2}
              onChange={(e) => setField('price_2', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Precio 3 · Distribuidor">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="—"
              value={form.price_3}
              onChange={(e) => setField('price_3', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Precio 4 · Especial">
            <input
              type="number"
              step="0.01"
              min="0"
              placeholder="—"
              value={form.price_4}
              onChange={(e) => setField('price_4', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Precio oferta">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.sale_price}
              onChange={(e) => setField('sale_price', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Precio regular" hint="Tachado en oferta">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.regular_price}
              onChange={(e) => setField('regular_price', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Costo (MXN)" hint="Interno / reportes">
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.cost}
              onChange={(e) => setField('cost', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="IVA (%)" hint="Interno / reportes">
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={form.iva}
              onChange={(e) => setField('iva', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Stock">
            <input
              type="number"
              step="1"
              min="0"
              value={form.stock}
              onChange={(e) => setField('stock', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Peso (kg)">
            <input
              type="number"
              step="0.001"
              min="0"
              value={form.weight_kg}
              onChange={(e) => setField('weight_kg', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </div>

      {/* Categoría / Marca */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Clasificación</h3>
        <div className="space-y-3">
          <Field label="Categoría">
            <input
              type="text"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
              placeholder="Buscar categoría… (ej. moto)"
              className={`${inputCls} mb-1.5`}
            />
            <select
              value={form.category_id}
              onChange={(e) => setField('category_id', e.target.value)}
              className={inputCls}
              size={catFilter ? 8 : undefined}
            >
              <option value="">Sin categoría</option>
              {catsFiltradas.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nivel && c.nivel > 0 ? `${'   '.repeat(c.nivel)}└ ` : ''}
                  {c.nombre}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Marca">
            <select
              value={form.brand_id}
              onChange={(e) => setField('brand_id', e.target.value)}
              className={inputCls}
            >
              <option value="">Sin marca</option>
              {marcas.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.nombre}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      {/* Imágenes / Galería */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-3">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink-900">Galería de imágenes</h3>
          <span className="text-[11px] text-ink-500">
            Arrastra archivos o haz click — la primera es la principal
          </span>
        </div>
        <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
          <ImageDropzone label="Principal" value={form.image} onChange={(v) => setField('image', v)} />
          <ImageDropzone label="Imagen 2" value={form.image2} onChange={(v) => setField('image2', v)} />
          <ImageDropzone label="Imagen 3" value={form.image3} onChange={(v) => setField('image3', v)} />
          <ImageDropzone label="Imagen 4" value={form.image4} onChange={(v) => setField('image4', v)} />
          <ImageDropzone label="Imagen 5" value={form.image5} onChange={(v) => setField('image5', v)} />
          <ImageDropzone label="Thumbnail" value={form.image_low} onChange={(v) => setField('image_low', v)} hint="250×250" />
        </div>
      </div>

      {/* Dimensiones */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Dimensiones</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Largo (cm)">
            <input type="number" step="0.001" min="0" value={form.dim_length} onChange={(e) => setField('dim_length', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Ancho (cm)">
            <input type="number" step="0.001" min="0" value={form.dim_width} onChange={(e) => setField('dim_width', e.target.value)} className={inputCls} />
          </Field>
          <Field label="Alto (cm)">
            <input type="number" step="0.001" min="0" value={form.dim_height} onChange={(e) => setField('dim_height', e.target.value)} className={inputCls} />
          </Field>
        </div>
        <div className="mt-3">
          <Field label="Medidas (texto libre)" hint='Ej: "50cm x 30cm x 20cm"'>
            <input type="text" value={form.dim_text} onChange={(e) => setField('dim_text', e.target.value)} className={inputCls} maxLength={200} />
          </Field>
        </div>
      </div>

      {/* Compatibilidad rápida */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Compatibilidad rápida</h3>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Año desde">
              <input type="text" value={form.year_from} onChange={(e) => setField('year_from', e.target.value)} className={inputCls} maxLength={4} placeholder="2010" />
            </Field>
            <Field label="Año hasta">
              <input type="text" value={form.year_to} onChange={(e) => setField('year_to', e.target.value)} className={inputCls} maxLength={4} placeholder="2024" />
            </Field>
          </div>
          <Field label="Marcas compatibles" hint="Texto libre, separado por coma">
            <input type="text" value={form.compat_brands} onChange={(e) => setField('compat_brands', e.target.value)} className={inputCls} maxLength={200} placeholder="Honda, Toyota, Nissan" />
          </Field>
          <Field label="Modelos compatibles" hint="Texto libre, separado por coma">
            <input type="text" value={form.compat_models} onChange={(e) => setField('compat_models', e.target.value)} className={inputCls} maxLength={200} placeholder="Civic, Corolla, Sentra" />
          </Field>
        </div>
      </div>

      {/* Origen y línea (autoparte) */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-2">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Autoparte — origen y marca</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Origen del repuesto" hint="Original, Genérico, Alternativo…">
            <input type="text" value={form.part_origin} onChange={(e) => setField('part_origin', e.target.value)} className={inputCls} maxLength={40} />
          </Field>
          <Field label="Línea" hint="Código corto (máx 10)">
            <input type="text" value={form.line} onChange={(e) => setField('line', e.target.value)} className={inputCls} maxLength={10} />
          </Field>
          <Field label="Marca del repuesto (texto)">
            <input type="text" value={form.part_brand} onChange={(e) => setField('part_brand', e.target.value)} className={inputCls} maxLength={40} placeholder="Bosch, Magnetti…" />
          </Field>
          <Field label="ID marca repuesto" hint="ID numérico si aplica">
            <input type="text" value={form.part_brand_id} onChange={(e) => setField('part_brand_id', e.target.value)} className={`${inputCls} font-mono`} />
          </Field>
        </div>
      </div>

      {/* Operación / historial */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">Operación e historial</h3>
        <div className="space-y-3">
          <Field label="ID proveedor">
            <input type="text" value={form.supplier_id} onChange={(e) => setField('supplier_id', e.target.value)} className={`${inputCls} font-mono`} />
          </Field>
          <Field label="SKU kit">
            <input type="text" value={form.sku_kit} onChange={(e) => setField('sku_kit', e.target.value)} className={`${inputCls} font-mono`} maxLength={100} />
          </Field>
          <Field label="SKU anterior" hint="Histórico — no buscable">
            <input type="text" value={form.previous_sku} onChange={(e) => setField('previous_sku', e.target.value)} className={`${inputCls} font-mono`} maxLength={30} />
          </Field>
          <Field label="Nombre anterior" hint="Para SEO y redirects">
            <input type="text" value={form.previous_name} onChange={(e) => setField('previous_name', e.target.value)} className={inputCls} maxLength={150} />
          </Field>
        </div>
      </div>

      {/* SEO */}
      <div className="rounded-2xl border border-ink-200 bg-white p-5 lg:col-span-3">
        <h3 className="mb-4 text-sm font-semibold text-ink-900">SEO</h3>
        <div className="space-y-3">
          <Field label="Meta título" hint={`${form.meta_title.length} / 150`}>
            <input
              type="text"
              value={form.meta_title}
              onChange={(e) => setField('meta_title', e.target.value)}
              maxLength={150}
              className={inputCls}
            />
          </Field>
          <Field label="Meta descripción">
            <textarea
              value={form.meta_description}
              onChange={(e) => setField('meta_description', e.target.value)}
              rows={3}
              className={inputCls}
            />
          </Field>
          <Field label="Meta keywords" hint="Palabras clave separadas por coma">
            <input
              type="text"
              value={form.meta_keywords}
              onChange={(e) => setField('meta_keywords', e.target.value)}
              className={inputCls}
            />
          </Field>
          <Field label="Tags" hint="Etiquetas internas, separadas por coma">
            <input
              type="text"
              value={form.tags}
              onChange={(e) => setField('tags', e.target.value)}
              className={inputCls}
            />
          </Field>
        </div>
      </div>
    </div>
  )
}


export const inputCls =
  'w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm text-ink-900 outline-none transition-colors focus:border-brand-500'

export function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <label className="block">
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-ink-700">
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </span>
        {hint && <span className="text-[10px] text-ink-500">{hint}</span>}
      </div>
      {children}
    </label>
  )
}

export function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (b: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-ink-100 p-3 transition-colors hover:bg-ink-50">
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
          checked ? 'bg-brand-600' : 'bg-ink-200'
        }`}
        role="switch"
        aria-checked={checked}
      >
        <span
          className={`inline-block size-4 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
      <div className="flex-1">
        <div className="text-sm font-medium text-ink-900">{label}</div>
        {hint && <div className="text-xs text-ink-500">{hint}</div>}
      </div>
    </label>
  )
}

export type { FormState as ProductFormState }

// ----------------------------------------------------------------------
// Sección "Productos relacionados" (compraron también)
// ----------------------------------------------------------------------

interface RecomendacionItem {
  id: string
  recommendedId: string
  source: string
  weight: number
  producto: {
    id: string
    sku: string
    nombre: string
    precio: number
    precioOferta: number | null
    stock: number
    imagen: string
    activo: boolean
  } | null
}

interface ProductoBusqueda {
  id: string
  sku: string
  nombre: string
  imagen: string
}

export function RecomendacionesSection({ productId }: { productId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debounced, setDebounced] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchTerm.trim()), 300)
    return () => clearTimeout(t)
  }, [searchTerm])

  const listQuery = useQuery<{ data: RecomendacionItem[] }>({
    queryKey: ['producto-recomendaciones', productId],
    queryFn: () => apiGet(`/api/admin/productos/${productId}/recomendaciones`),
    enabled: open,
  })

  const searchQuery = useQuery<{ data: ProductoBusqueda[] }>({
    queryKey: ['productos-search-rec', debounced],
    queryFn: () => apiGet(`/api/admin/productos?page=1&q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2,
    select: (d: any) => ({
      data: (d.data ?? []).map((p: any) => ({
        id: p.id,
        sku: p.sku,
        nombre: p.nombre,
        imagen: p.imagen,
      })),
    }),
  })

  const addMut = useMutation({
    mutationFn: (recommendedId: string) =>
      apiPost(`/api/admin/productos/${productId}/recomendaciones`, { recommendedId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto-recomendaciones', productId] })
      setSearchTerm('')
    },
  })

  const removeMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/recomendaciones/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['producto-recomendaciones', productId] })
    },
  })

  const items = listQuery.data?.data ?? []
  const existingIds = useMemo(
    () => new Set(items.map((it) => it.recommendedId)),
    [items],
  )

  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded-2xl p-5 text-left hover:bg-ink-50"
      >
        <div className="flex items-center gap-2">
          <Link2 className="size-4 text-ink-600" />
          <h3 className="text-sm font-semibold text-ink-900">Productos relacionados</h3>
          {open && items.length > 0 && (
            <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs text-ink-600">
              {items.length}
            </span>
          )}
        </div>
        {open ? <ChevronDown className="size-4 text-ink-500" /> : <ChevronRight className="size-4 text-ink-500" />}
      </button>

      {open && (
        <div className="border-t border-ink-100 p-5">
          <p className="mb-3 text-xs text-ink-500">
            Productos que aparecerán como "Compraron también". Puedes agregarlos manualmente o usar el
            botón Auto-generar en la lista de Productos para crearlos a partir del historial de pedidos.
          </p>

          {/* Buscador para agregar */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
              <input
                type="search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar producto para agregar (SKU o nombre)…"
                className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
              />
            </div>

            {searchQuery.data && searchQuery.data.data.length > 0 && debounced.length >= 2 && (
              <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-ink-200 bg-white">
                {searchQuery.data.data
                  .filter((p) => p.id !== productId && !existingIds.has(p.id))
                  .slice(0, 10)
                  .map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addMut.mutate(p.id)}
                      disabled={addMut.isPending}
                      className="flex w-full items-center gap-3 border-b border-ink-100 px-3 py-2 text-left text-sm last:border-0 hover:bg-ink-50 disabled:opacity-50"
                    >
                      <div className="grid size-10 place-items-center overflow-hidden rounded bg-ink-100">
                        {p.imagen ? (
                          <img
                            src={productImageUrl(p.imagen)}
                            alt={p.nombre}
                            className="size-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-[10px] text-ink-500">—</span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-ink-900">{p.nombre}</div>
                        <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                      </div>
                      <Plus className="size-4 text-brand-600" />
                    </button>
                  ))}
              </div>
            )}

            {addMut.isError && (
              <p className="mt-2 text-xs text-red-700">{(addMut.error as Error).message}</p>
            )}
          </div>

          {/* Lista actual */}
          {listQuery.isLoading ? (
            <div className="grid place-items-center py-10 text-ink-500">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-lg border border-dashed border-ink-200 p-6 text-center text-sm text-ink-500">
              Aún no hay recomendaciones. Usa el buscador para agregar o haz click en Auto-generar desde la lista de productos.
            </div>
          ) : (
            <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
              {items.map((it) => (
                <li key={it.id} className="flex items-center gap-3 p-3">
                  <div className="grid size-10 place-items-center overflow-hidden rounded bg-ink-100">
                    {it.producto?.imagen ? (
                      <img
                        src={productImageUrl(it.producto.imagen)}
                        alt={it.producto.nombre}
                        className="size-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-[10px] text-ink-500">—</span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink-900">
                      {it.producto?.nombre ?? `Producto #${it.recommendedId} (eliminado)`}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-ink-500">
                      {it.producto && (
                        <span className="font-mono">{it.producto.sku}</span>
                      )}
                      {it.producto && (
                        <span>· {formatMXN(it.producto.precio)}</span>
                      )}
                      <span>· peso {it.weight}</span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                          it.source === 'manual'
                            ? 'bg-brand-100 text-brand-800'
                            : 'bg-ink-100 text-ink-700'
                        }`}
                      >
                        {it.source === 'manual' ? 'Manual' : 'Auto'}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (window.confirm(`¿Quitar recomendación "${it.producto?.nombre ?? it.recommendedId}"?`)) {
                        removeMut.mutate(it.id)
                      }
                    }}
                    disabled={removeMut.isPending}
                    className="rounded-md p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    title="Eliminar"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

// ----------------------------------------------------------------------
// Sección colapsable de variantes
// ----------------------------------------------------------------------

interface Variante {
  id: string
  productId: string
  sku: string
  nombre: string
  atributos: string | null
  precio: number | null
  precioOferta: number | null
  stock: number
  imagen: string
  activa: boolean
  orden: number
}

interface VarianteFormState {
  sku: string
  nombre: string
  precio: string
  precioOferta: string
  stock: string
  imagen: string
  atributos: Array<{ key: string; value: string }>
}

const emptyVarianteForm: VarianteFormState = {
  sku: '',
  nombre: '',
  precio: '',
  precioOferta: '',
  stock: '0',
  imagen: '',
  atributos: [{ key: '', value: '' }],
}

function VariantesSection({ productId }: { productId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<Variante | null>(null)
  const [form, setForm] = useState<VarianteFormState>(emptyVarianteForm)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ data: Variante[] }>({
    queryKey: ['variantes', productId],
    queryFn: () => apiGet(`/api/admin/productos/${productId}/variantes`),
    enabled: open,
  })

  const saveMutation = useMutation({
    mutationFn: async (payload: { id: string | null; data: any }) => {
      if (payload.id) {
        return apiPut(`/api/admin/variantes/${payload.id}`, payload.data)
      }
      return apiPost(`/api/admin/productos/${productId}/variantes`, payload.data)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['variantes', productId] })
      closeModal()
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/variantes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['variantes', productId] }),
    onError: (e: Error) => alert(e.message),
  })

  function openCreate() {
    setEditing(null)
    setForm(emptyVarianteForm)
    setError(null)
    setModalOpen(true)
  }

  function openEdit(v: Variante) {
    let parsedAttrs: Array<{ key: string; value: string }> = [{ key: '', value: '' }]
    if (v.atributos) {
      try {
        const obj = JSON.parse(v.atributos) as Record<string, string>
        const entries = Object.entries(obj).map(([k, val]) => ({ key: k, value: String(val) }))
        if (entries.length > 0) parsedAttrs = entries
      } catch {
        // ignore parse error
      }
    }
    setEditing(v)
    setForm({
      sku: v.sku,
      nombre: v.nombre,
      precio: v.precio !== null ? String(v.precio) : '',
      precioOferta: v.precioOferta !== null ? String(v.precioOferta) : '',
      stock: String(v.stock),
      imagen: v.imagen,
      atributos: parsedAttrs,
    })
    setError(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditing(null)
    setError(null)
    setForm(emptyVarianteForm)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const attrsObj: Record<string, string> = {}
    for (const a of form.atributos) {
      const k = a.key.trim()
      if (k) attrsObj[k] = a.value.trim()
    }
    const payload = {
      sku: form.sku.trim(),
      name: form.nombre.trim(),
      attributes_json: Object.keys(attrsObj).length > 0 ? JSON.stringify(attrsObj) : null,
      price: form.precio !== '' ? Number(form.precio) : null,
      sale_price: form.precioOferta !== '' ? Number(form.precioOferta) : null,
      stock: parseInt(form.stock) || 0,
      image: form.imagen.trim() || null,
    }
    if (!payload.sku || !payload.name) {
      setError('SKU y nombre son obligatorios')
      return
    }
    saveMutation.mutate({ id: editing?.id ?? null, data: payload })
  }

  function onDelete(v: Variante) {
    if (confirm(`¿Eliminar la variante "${v.nombre}"?`)) {
      deleteMutation.mutate(v.id)
    }
  }

  function updateAttr(idx: number, field: 'key' | 'value', value: string) {
    setForm((f) => {
      const next = [...f.atributos]
      next[idx] = { ...next[idx]!, [field]: value }
      return { ...f, atributos: next }
    })
  }

  function addAttr() {
    setForm((f) => ({ ...f, atributos: [...f.atributos, { key: '', value: '' }] }))
  }

  function removeAttr(idx: number) {
    setForm((f) => ({
      ...f,
      atributos: f.atributos.filter((_, i) => i !== idx),
    }))
  }

  const variantes = data?.data ?? []

  return (
    <div className="rounded-2xl border border-ink-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between px-5 py-4 text-left hover:bg-ink-50"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="size-4 text-ink-500" /> : <ChevronRight className="size-4 text-ink-500" />}
          <Layers className="size-4 text-brand-600" />
          <span className="text-sm font-semibold text-ink-900">Variantes</span>
          {data && (
            <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-medium text-ink-700">
              {variantes.length}
            </span>
          )}
        </div>
        <span className="text-xs text-ink-500">{open ? 'Ocultar' : 'Mostrar'}</span>
      </button>

      {open && (
        <div className="border-t border-ink-200 p-5">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs text-ink-500">
              Variantes (color, talla, etc.) que heredan datos del producto padre si no se especifican.
            </p>
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Agregar variante
            </button>
          </div>

          {isLoading && (
            <div className="grid place-items-center py-10 text-ink-500">
              <Loader2 className="size-6 animate-spin" />
            </div>
          )}

          {data && variantes.length === 0 && (
            <div className="rounded-lg border border-dashed border-ink-200 p-6 text-center text-sm text-ink-400">
              No hay variantes para este producto
            </div>
          )}

          {variantes.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                    <th className="py-2 pr-3 font-medium">SKU</th>
                    <th className="py-2 pr-3 font-medium">Nombre</th>
                    <th className="py-2 pr-3 font-medium">Atributos</th>
                    <th className="py-2 pr-3 text-right font-medium">Precio</th>
                    <th className="py-2 pr-3 text-right font-medium">Stock</th>
                    <th className="w-20 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {variantes.map((v) => (
                    <tr key={v.id} className="border-t border-ink-100">
                      <td className="py-2 pr-3 font-mono text-xs text-ink-700">{v.sku}</td>
                      <td className="py-2 pr-3 font-medium text-ink-900">{v.nombre}</td>
                      <td className="py-2 pr-3 text-xs text-ink-600">
                        {v.atributos ? (
                          <code className="rounded bg-ink-50 px-1.5 py-0.5">{v.atributos}</code>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right text-ink-700">
                        {v.precio !== null ? `$${v.precio}` : <span className="text-ink-400">hereda</span>}
                      </td>
                      <td className="py-2 pr-3 text-right text-ink-700">{v.stock}</td>
                      <td className="py-2">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(v)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Editar"
                          >
                            <EditIcon className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(v)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                            title="Eliminar"
                            disabled={deleteMutation.isPending}
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {modalOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-6">
          <form onSubmit={onSubmit} className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink-900">
                {editing ? 'Editar variante' : 'Nueva variante'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-ink-500 hover:bg-ink-100"
              >
                <X className="size-4" />
              </button>
            </div>
            {error && (
              <div className="mb-3 rounded-lg bg-red-50 p-2 text-xs text-red-700">{error}</div>
            )}
            <div className="grid gap-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="SKU" required>
                  <input
                    type="text"
                    value={form.sku}
                    onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                    className={`${inputCls} font-mono`}
                    maxLength={80}
                    required
                  />
                </Field>
                <Field label="Nombre" required>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                    className={inputCls}
                    maxLength={150}
                    required
                  />
                </Field>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <Field label="Precio" hint="vacío = hereda">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.precio}
                    onChange={(e) => setForm((f) => ({ ...f, precio: e.target.value }))}
                    className={inputCls}
                  />
                </Field>
                <Field label="Precio oferta">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.precioOferta}
                    onChange={(e) => setForm((f) => ({ ...f, precioOferta: e.target.value }))}
                    className={inputCls}
                  />
                </Field>
                <Field label="Stock">
                  <input
                    type="number"
                    step="1"
                    min="0"
                    value={form.stock}
                    onChange={(e) => setForm((f) => ({ ...f, stock: e.target.value }))}
                    className={inputCls}
                  />
                </Field>
              </div>

              <Field label="Imagen (URL)">
                <input
                  type="text"
                  value={form.imagen}
                  onChange={(e) => setForm((f) => ({ ...f, imagen: e.target.value }))}
                  className={inputCls}
                  maxLength={255}
                  placeholder="https://… (opcional)"
                />
              </Field>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-ink-700">Atributos (key / value)</span>
                  <button
                    type="button"
                    onClick={addAttr}
                    className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-700"
                  >
                    <Plus className="size-3" /> Añadir
                  </button>
                </div>
                <div className="space-y-2">
                  {form.atributos.map((a, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={a.key}
                        onChange={(e) => updateAttr(idx, 'key', e.target.value)}
                        placeholder="color"
                        className={`${inputCls} flex-1`}
                      />
                      <input
                        type="text"
                        value={a.value}
                        onChange={(e) => updateAttr(idx, 'value', e.target.value)}
                        placeholder="rojo"
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => removeAttr(idx)}
                        className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeModal}
                className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saveMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saveMutation.isPending && <Loader2 className="size-3.5 animate-spin" />}
                Guardar
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  )
}
